import { HeightUnit } from "@/Common/Utilities/Units/Units";
import { BritishColumbiaObstetricalFilePage, BritishColumbiaObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/BritishColumbiaObstetricalFilePage";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { BritishColumbiaObstetricalFileSection1 } from "./Subcomponent/BritishColumbiaObstetricalFileSection1";
import { BritishColumbiaObstetricalFileSection2 } from "./Subcomponent/BritishColumbiaObstetricalFileSection2";
import { BritishColumbiaObstetricalFileSection3 } from "./Subcomponent/BritishColumbiaObstetricalFileSection3";
import { BritishColumbiaObstetricalFileSection4 } from "./Subcomponent/BritishColumbiaObstetricalFileSection4";
import { BritishColumbiaObstetricalFileSection5 } from "./Subcomponent/BritishColumbiaObstetricalFileSection5";
import { BritishColumbiaObstetricalFileSection6 } from "./Subcomponent/BritishColumbiaObstetricalFileSection6";
import { BritishColumbiaObstetricalFileSection7 } from "./Subcomponent/BritishColumbiaObstetricalFileSection7";
import { BritishColumbiaObstetricalFileSection8 } from "./Subcomponent/BritishColumbiaObstetricalFileSection8";
import { BritishColumbiaObstetricalFileSection9 } from "./Subcomponent/BritishColumbiaObstetricalFileSection9";
import { BritishColumbiaObstetricalFileSection10 } from "./Subcomponent/BritishColumbiaObstetricalFileSection10";
import { BritishColumbiaObstetricalFileSection11 } from "./Subcomponent/BritishColumbiaObstetricalFileSection11";

class BritishColumbiaObstetricalFilePart1State extends BritishColumbiaObstetricalFilePageState {
    public vitalSign: ObstetricalFileVitalSign;
}

class BritishColumbiaObstetricalFilePart1 extends BritishColumbiaObstetricalFilePage<BritishColumbiaObstetricalFilePart1State> {
    private readonly componentId = "british-columbia-obstetrical-file-part1";
    private pendingFormUpdate = false;

    public componentDidMount(): void {
        if (this.pendingFormUpdate) {
            this.handleFormUpdate();
            this.pendingFormUpdate = false;
        }

        if (this.state.form.Section10.VitalSignEmrTemplateId)
            this.handleObstetricalFileVitalSignLink(this.state.form.Section10.VitalSignEmrTemplateId);
    }

    protected buildState(props: IObstetricalFilePageProps<BritishColumbiaObstetricalFileForm>): BritishColumbiaObstetricalFilePart1State {
        const state = new BritishColumbiaObstetricalFilePart1State();
        state.form = new BritishColumbiaObstetricalFileForm(props.form);
        const examinationSection = state.form.Section10;
        state.vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
            obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
            emrTemplateId: examinationSection.VitalSignEmrTemplateId,
            consultationDate: props.encounter.ConsultationDate,
            obstetricalFileContributionDate: props.contributionCreationDate,
            skipLoadLastHour:
                state.form.Section17?.any()
                && !!state.form.Section17[0].EmrTemplateId
                && props.obstetricalFileMedicalSummary.getVitalSignBy(state.form.Section17[0].EmrTemplateId, props.encounter.ConsultationDate) != null,
            weightUnit: examinationSection.WeightUnit,
            heightUnit: examinationSection.HeightUnit
        });

        if (state.form.isVitalSignReusedInPart2())
            state.form.Section17[0].EmrTemplateId = state.vitalSign?.emrTemplateId;

        if (!state.form.Section17.any()) {
            this.initializePrenatalVisit(state.form, props);
            this.pendingFormUpdate = true;
        }

        state.form.Section10.VitalSignEmrTemplateId = state.vitalSign?.emrTemplateId;
        return state;
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId} className="bc-antenatal-part1">
                <BritishColumbiaObstetricalFileSection1 {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection2 {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection3 {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection4 {...this.getSubcomponentBaseProps()} />
                {this.renderSection5To9()}
                {this.renderSection10And11()}
            </div>
        );
    }

    private renderSection5To9(): JSX.Element {
        return (
            <div className="flex-display border border-bottom" style={{ justifyContent: "space-evenly" }}>
                <div className="border border-right" style={{ width: "33.3333333%" }}>
                    <BritishColumbiaObstetricalFileSection5 {...this.getSubcomponentBaseProps()} />
                    <BritishColumbiaObstetricalFileSection6 {...this.getSubcomponentBaseProps()} />
                </div>
                <div className="border border-right" style={{ width: "33.3333333%" }}>
                    <BritishColumbiaObstetricalFileSection7 {...this.getSubcomponentBaseProps()} />
                </div>
                <div style={{ width: "33.3333333%" }}>
                    <BritishColumbiaObstetricalFileSection8 {...this.getSubcomponentBaseProps()} />
                    <BritishColumbiaObstetricalFileSection9 {...this.getSubcomponentBaseProps()} />
                </div>
            </div>
        );
    }

    protected async onVitalSignUpdated(): Promise<void> {
        let updatedVitalSign: MedicalItemVitalSign = await super.handleVitalSignUpdate(
            this.state.vitalSign,
            this.state.form.Section10.VitalSignEmrTemplateId
        );
        this.mutateState(
            s => {
                s.vitalSign.updateValueFromMedicalItemVitalSign(updatedVitalSign, s.form.Section10.WeightUnit, s.form.Section10.HeightUnit);
                s.form.Section10.VitalSignEmrTemplateId = updatedVitalSign?.EMRTemplateId;
            },
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }

    private renderSection10And11(): JSX.Element {
        return (
            <div className="border border-bottom flex-display">
                <div className="border border-right" style={{ width: "66.66666%" }}>
                    <BritishColumbiaObstetricalFileSection10
                        {...this.getSubcomponentBaseProps()}
                        vitalSign={this.state.vitalSign}
                        onVitalSignUpdated={() => this.onVitalSignUpdated()}
                        mutateVitalSignAndForm={(mutation, triggerVitalSignUpdate) => {
                            this.mutateModelState(
                                s => mutation(s),
                                () => triggerVitalSignUpdate && this.onVitalSignUpdated()
                            );
                        }}
                        getHeightDropdownOptions={(unit: HeightUnit, isInCentimeter?: boolean) => this.getHeightDropdownOptions(unit, isInCentimeter)}
                    />
                </div>
                <div style={{ width: "33.3333%" }}>
                    <BritishColumbiaObstetricalFileSection11 {...this.getSubcomponentBaseProps()} />
                </div>
            </div>
        );
    }
}

export { BritishColumbiaObstetricalFilePart1, BritishColumbiaObstetricalFilePart1State };
